import { useEffect, useState } from "react";
import { api, fmtCurrency, fmtNumber } from "@/lib/api";
import { useCapital } from "@/context/CapitalContext";
import { TID } from "@/constants/testIds";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { AIPanel } from "@/components/capital/AskAssetAI";

export function AssetClassForecast() {
  const { client } = useCapital();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!client) return;
    setLoading(true);
    api.get("/capital/asset-class-forecast", { params: { client } }).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [client]);

  return (
    <>
    <AIPanel screen="classes" />
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="bg-white border border-slate-200 rounded-lg p-5">
        <h3 className="font-display text-lg font-semibold text-slate-900">Renewal spend by Asset Class</h3>
        <p className="text-xs text-slate-500 mb-4">Immediate+12M vs 24M split</p>
        <div className="h-96">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} layout="vertical" margin={{ top: 5, right: 20, left: 5, bottom: 0 }}>
              <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" tick={{ fill: "#64748b", fontSize: 11 }} tickFormatter={(v) => `$${Math.round(v/1000)}k`} axisLine={{ stroke: "#e2e8f0" }} tickLine={false} />
              <YAxis type="category" dataKey="asset_group" tick={{ fill: "#334155", fontSize: 11 }} axisLine={false} tickLine={false} width={140} />
              <Tooltip formatter={(v) => `$${Number(v).toLocaleString()}`} contentStyle={{ fontSize: 12, borderRadius: 6, borderColor: "#e2e8f0" }} />
              <Legend wrapperStyle={{ fontSize: 11 }} iconSize={10} />
              <Bar dataKey="immediate_12m_budget" name="Immediate + 12M" stackId="a" fill="#e11d48" maxBarSize={14} />
              <Bar dataKey="twenty_four_m_budget" name="24 Month" stackId="a" fill="#004a6b" maxBarSize={14} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="px-5 pt-5 pb-3">
          <h3 className="font-display text-lg font-semibold text-slate-900">Asset Class Forecast</h3>
          <p className="text-xs text-slate-500">Portfolio composition and renewal budget</p>
        </div>
        <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-slate-50 z-10">
              <tr className="text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="text-left font-medium px-4 py-2.5">Asset Class</th>
                <th className="text-right font-medium px-4 py-2.5">Count</th>
                <th className="text-right font-medium px-4 py-2.5">Imm+12M</th>
                <th className="text-right font-medium px-4 py-2.5">24M</th>
                <th className="text-right font-medium px-4 py-2.5">Total</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="p-4"><div className="h-6 rounded shimmer" /></td></tr>
              ) : (
                data.map((g) => (
                  <tr key={g.asset_group} data-testid={TID.capitalGroupRow(g.asset_group)} className="border-b border-slate-100 hover:bg-slate-50/70">
                    <td className="px-4 py-2.5 text-slate-800">{g.asset_group}</td>
                    <td className="px-4 py-2.5 text-right text-slate-600 tabular-nums">{fmtNumber(g.asset_count)}</td>
                    <td className="px-4 py-2.5 text-right text-slate-600 tabular-nums">{fmtCurrency(g.immediate_12m_budget)}</td>
                    <td className="px-4 py-2.5 text-right text-slate-600 tabular-nums">{fmtCurrency(g.twenty_four_m_budget)}</td>
                    <td className="px-4 py-2.5 text-right font-medium text-slate-900 tabular-nums">{fmtCurrency(g.total_renewal_budget)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
    </>
  );
}
